import React, { useEffect, useState } from 'react';
import { compose, pick, prop } from 'ramda';
import { uniqueId } from '@core/js/utils/methods.util';
import { withDynamicReducer, useDynamicReducerDispatch, useDynamicReducerSelector } from '@core/js/components/common/Slider/store';
import { useDeviceSelector } from '@core/js/store/hooks';

import * as hooks from '@core/js/components/common/Slider/hooks';
import Controls from '@core/js/components/common/Slider/Controls';
import * as actions from '@core/js/components/common/Slider/store/actions';
import SlidesAnimation from '@core/js/components/common/Slider/SlidesAnimation';
import ProgressBar from '@core/js/components/common/Slider/ProgressBar';

import style from './index.scss';

export const Slider = (props) => {
    const {
        options: {
            controls,
            ...options
        } = {},
        slideWidth,
        children,
        autoRotate = false,
        renderAll = true,
        infinite = autoRotate || false,
        showControls = true,
        showControlsOnHover = false,
        swipe = true,
        className = '',
        classNames = {},
        styles = {},
        refs = {},
        handlerSliderTransitionEnd,
        onActiveSlideChange,
        isBulletsClickable,
        isAllItemsVisible = false,
        showDisabledArrows = false,
        showControlsAtStart = false
    } = props;
    const sliderDispatch = useDynamicReducerDispatch();
    const { activeSlide, prevSlide, direction, slidesCount } = useDynamicReducerSelector(pick(['activeSlide', 'prevSlide', 'direction', 'slidesCount']));
    const [isHover, setIsHover] = useState(false);
    const isAutoRotateDisabled = options.animation?.displayTime === 0;
    const isProgressBarAtTop = controls?.progressBar?.enabled && controls?.progressBar?.position === 'top';
    const isProgressBarAtBottom = controls?.progressBar?.enabled && controls?.progressBar?.position === 'bottom';
    const {
        onMouseEnter,
        onMouseLeave
    } = hooks.useSliderMouseEventHandlers({ slidesCount, showControlsOnHover, setIsHover });

    useEffect(() => {
        sliderDispatch(actions.addSlider({ slidesCount: children.length }));
    }, [children.length, sliderDispatch]);

    hooks.useActiveSlideChangeEffect({ onActiveSlideChange,
        sliderState: { activeSlide, prevSlide, direction, slidesCount } });

    const isDesktop = useDeviceSelector(prop('isDesktop'));
    const doShowControls = showControls && slidesCount > 1 && (showControlsOnHover ? isDesktop ? isHover : true : true);

    if (!slidesCount) {
        return null;
    }

    const controlsContent = (
        <Controls
            options={controls}
            infinite={infinite}
            classNames={classNames}
            activeSlide={activeSlide}
            slidesCount={slidesCount}
            showDisabledArrows={showDisabledArrows}
            isBulletsClickable={isBulletsClickable}
        />
    );

    return (
        <>
            {isProgressBarAtTop && slidesCount > 1 && (
                <ProgressBar classNames={classNames} activeSlide={activeSlide} slidesCount={slidesCount} />
            )}
            <div
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                className={`${style.component} ${className}`}
            >
                {doShowControls && showControlsAtStart && controlsContent}
                <SlidesAnimation options={options}
                    sliderId={uniqueId(slidesCount)} /* TotalCasino changes */
                    autoRotate={autoRotate && slidesCount > 1 && !isAutoRotateDisabled}
                    isSwipeEnabled={swipe && slidesCount > 1}
                    slideRef={refs.slide}
                    sliderRef={refs.slider}
                    classNames={classNames}
                    styles={styles}
                    activeSlide={showControls ? activeSlide : 0}
                    prevSlide={prevSlide}
                    direction={direction}
                    slidesCount={slidesCount}
                    slideWidth={slideWidth}
                    renderAll={renderAll}
                    handlerSliderTransitionEnd={handlerSliderTransitionEnd}
                    isAllItemsVisible={isAllItemsVisible}
                    infinite={infinite}>
                    {children}
                </SlidesAnimation>

                {doShowControls && !showControlsAtStart && controlsContent}
            </div>
            {isProgressBarAtBottom && slidesCount > 1 && (
                <ProgressBar classNames={classNames} activeSlide={activeSlide} slidesCount={slidesCount} />
            )}
        </>
    );
};

export default compose(
    withDynamicReducer()
)(Slider);
